// --- React & Redux
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
// --- Actions & Hooks
import { useOfferHistoryActions } from "../../store/offerHistory/useOfferHistory";
import { useOffersActions } from "../../store/offers/useOffers";
import { useLoading } from "../../utils/hooks/useLoading";
import { useToasts } from "../../utils/hooks/useToasts";
// --- External UI Libraries
import { Col, Row } from "antd";
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
// --- Components
import TwoRowsLayout from "../Offers/two-row";
import { OfferHistorySearchForm } from "./components/search-form";
import { OfferHistoryGrid } from "./components/grid";
import { ColumnsDrawer } from "./components/drawers/columns";
import { FiltersSidebar } from "../Offers/components/filters-sidebar";
import ToastNotificationProvider from "../../components/ToastNotifications";
// --- Config
import {
  COLUMN_OPTIONS,
  FILTER_COLUMNS,
} from "./components/grid/config/columns";
// --- Utils
import { tapApi } from "../../api";
import { buildOffersReturnHref } from "../../utils/buildOfferHistoryUrl";
// --- Styles
import "./styles.scss";

/**
 * Offer History Page
 *
 * Features:
 * - Displays history for a single selected offer
 * - Read-only view of offer changes over time
 * - Deep link: `/offerHistory?jobId=&offerId=&page=` (also opened from Offers history link)
 *
 * @returns {Element}
 */
export const OfferHistoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const gridRef = useRef(null);
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { updateOffers } = useOffersActions();

  const { selectedOffer, offerTableHistory } = useSelector(
    (state) => state.offers,
  );
  const { internalFilters } = useSelector((state) => state.offerHistory);
  const {
    setOfferHistoryFilters,
    loadOfferHistoryFilters,
    clearOfferHistoryFilters,
  } = useOfferHistoryActions();

  const [searchAll, setSearchAll] = useState("");
  const [, forceUpdate] = useState(0);

  const urlOfferId = searchParams.get("offerId");
  const urlJobId = searchParams.get("jobId");

  const historyMatchesUrl =
    urlOfferId && String(selectedOffer?.id) === String(urlOfferId);
  const historyForGrid = historyMatchesUrl ? offerTableHistory : [];

  useEffect(() => {
    if (!urlOfferId) return;

    let cancelled = false;

    // Clear stale history from a prior offer before fetching
    updateOffers({
      offerTableHistory: [],
      selectedOffer: {
        id: urlOfferId,
        ...(urlJobId ? { licensingJobId: urlJobId } : {}),
      },
    });

    const loadHistory = async () => {
      setLoadingTrue();
      try {
        const data = await tapApi.offerTableHistory(urlOfferId);

        if (cancelled) return;

        if (data && !data.code && !data.httpCode && !data.error) {
          updateOffers({
            selectedOffer: {
              id: urlOfferId,
              ...(urlJobId ? { licensingJobId: urlJobId } : {}),
            },
            offerTableHistory: data,
          });
        } else {
          console.error("Failed to fetch offer history:", data?.error);
          showErrorToast("Failed to fetch offer history.");
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching offer history:", error);
          showErrorToast(error?.message || "Error fetching offer history.");
        }
      } finally {
        if (!cancelled) {
          setLoadingFalse();
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [
    urlOfferId,
    urlJobId,
    updateOffers,
    setLoadingTrue,
    setLoadingFalse,
    showErrorToast,
  ]);

  useEffect(() => {
    if (!urlOfferId) return;
    setSearchAll("");
    setOfferHistoryFilters({}, null);
    loadOfferHistoryFilters(urlOfferId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlOfferId]);

  const [hiddenColumns, setHiddenColumns] = useState(() => {
    return COLUMN_OPTIONS.filter((col) => col.defaultHidden).map(
      (col) => col.field,
    );
  });

  const [columnsDrawerOpen, setColumnsDrawerOpen] = useState(false);

  const handleBack = () => {
    if (location.state?.fromOffers) {
      navigate(-1);
      return;
    }

    const offersHref = buildOffersReturnHref(
      urlJobId,
      searchParams.get("page"),
    );

    if (offersHref) {
      navigate(offersHref);
      return;
    }

    navigate(-1);
  };

  const handleSearchChange = (value) => {
    setSearchAll(value);
  };

  const handleClearFilters = () => {
    gridRef.current?.handleClearFilters();
    clearOfferHistoryFilters(urlOfferId);
    forceUpdate((n) => n + 1);
  };

  /**
   * Remove a specific filter value from a field
   */
  const handleRemoveFilter = (field, valueToRemove) => {
    const currentValues = internalFilters[field] || [];
    const newValues = currentValues.filter(
      (opt) => opt.value !== valueToRemove,
    );
    const newFilters = { ...internalFilters, [field]: newValues };
    // Clean up empty arrays
    if (newValues.length === 0) {
      delete newFilters[field];
    }
    setOfferHistoryFilters(newFilters, urlOfferId);
    forceUpdate((n) => n + 1);
  };

  const handleToggleColumn = (field) => {
    setHiddenColumns((prev) => {
      if (prev.includes(field)) {
        return prev.filter((f) => f !== field);
      }
      return [...prev, field];
    });
  };

  const offerDisplay = urlOfferId ? `Offer ${urlOfferId}` : "";

  const activeFilterCount = gridRef.current?.activeFilterCount || 0;

  return (
    <>
      <Row className="offer-history-page-header">
        <Col>
          <Button
            type="button"
            styleType="link"
            iconType="fa-solid fa-arrow-left"
            onClick={handleBack}
          >
            Back
          </Button>
          <Typography variant="T50" className="offer-history-page-title">
            Offer History {offerDisplay && `- ${offerDisplay}`}
          </Typography>
        </Col>
        <Col className="offer-history-page-actions">
          <Button
            type="button"
            styleType="link"
            iconType="fa-regular fa-table-columns"
            onClick={() => setColumnsDrawerOpen(true)}
          >
            Columns
          </Button>
          <Button
            type="button"
            styleType="link"
            iconType="fa-regular fa-download"
            onClick={() => gridRef.current?.handleExport()}
          >
            Download
          </Button>
          <Button
            type="button"
            styleType="link"
            iconType="fa-regular fa-print"
            onClick={() => gridRef.current?.handlePrint()}
          >
            Print
          </Button>
        </Col>
      </Row>

      <div className="offer-history-page-content">
        <TwoRowsLayout>
          <main data-position="top">
            <OfferHistorySearchForm
              searchAll={searchAll}
              onSearchChange={handleSearchChange}
              internalFilters={internalFilters}
              filterColumns={gridRef.current?.FILTER_COLUMNS || FILTER_COLUMNS}
              onRemoveFilter={handleRemoveFilter}
            />
          </main>
          <main data-position="bottom">
            <OfferHistoryGrid
              ref={gridRef}
              historyData={historyForGrid}
              selectedOffer={
                urlOfferId
                  ? {
                      id: urlOfferId,
                      ...(urlJobId ? { licensingJobId: urlJobId } : {}),
                    }
                  : selectedOffer
              }
              searchAll={searchAll}
              hiddenColumns={hiddenColumns}
            />
          </main>
        </TwoRowsLayout>

        <FiltersSidebar
          filterColumns={gridRef.current?.FILTER_COLUMNS || FILTER_COLUMNS}
          filterOptions={gridRef.current?.filterOptions || {}}
          internalFilters={internalFilters}
          onFilterChange={(field, values) =>
            gridRef.current?.handleFilterChange(field, values)
          }
          onClearFilters={handleClearFilters}
          activeFilterCount={activeFilterCount}
        />
      </div>

      <ColumnsDrawer
        open={columnsDrawerOpen}
        onClose={() => setColumnsDrawerOpen(false)}
        columnOptions={COLUMN_OPTIONS}
        hiddenColumns={hiddenColumns}
        onToggleColumn={handleToggleColumn}
      />

      <ToastNotificationProvider />
    </>
  );
};
